import { useState } from "react";
import { X } from "lucide-react";
import type { InterviewCategory, CategoryCreate, CategoryUpdate } from "../api/InterviewQaApi";

interface CategoryFormProps {
  initial?: InterviewCategory | null;
  onSave: (data: CategoryCreate | CategoryUpdate) => Promise<void>;
  onClose: () => void;
}

export default function CategoryForm({ initial, onSave, onClose }: CategoryFormProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError("Name is required"); return; }
    setSaving(true);
    try {
      await onSave({ name: name.trim(), description: description.trim() || undefined });
      onClose();
    } catch {
      setError("Failed to save category");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground font-inter">
            {initial ? "Edit Category" : "New Category"}
          </h2>
          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-surface-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Name *</label>
            <input
              value={name}
              onChange={(e) => { setName(e.target.value); setError(""); }}
              className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-inter"
              placeholder="e.g. React.js, System Design"
            />
            {error && <p className="text-xs text-destructive mt-1">{error}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Description</label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary resize-none font-inter"
              placeholder="Brief description of this category…"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-xl hover:bg-surface-muted font-inter">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-sm font-medium rounded-xl font-inter"
            >
              {saving ? "Saving…" : initial ? "Save Changes" : "Create Category"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
